import {
  OAuthProviderType,
  ServerDeploymentType,
  ServerFeature,
} from '@affine/graphql';

import type { ServerConfig, ServerMetadata } from './types';

const PIPE_PRODUCTION_ORIGIN = 'https://app.pipe.monster';

function nativePipeOrBrowserOrigin() {
  return BUILD_CONFIG.isNative ? PIPE_PRODUCTION_ORIGIN : location.origin;
}

export const BUILD_IN_SERVERS: (ServerMetadata & { config: ServerConfig })[] =
  environment.isSelfHosted
    ? [
        {
          id: 'pipe-cloud',
          // Native shells must never inherit an upstream host from a build
          // channel. Browser self-host deployments keep their own origin.
          baseUrl: nativePipeOrBrowserOrigin(),
          config: {
            // PIPE: client-side initial config (the real name/features come from the backend's
            // serverConfig once it loads). CAUTION — `environment.isSelfHosted` is a RUNTIME flag
            // read from the `env:isSelfHosted` meta tag that only selfhost.html carries. The dev
            // server takes this branch (dev-web.sh exports SELF_HOSTED=true -> selfhost.html), but
            // the STATIC production bundle behind Caddy serves index.html (no meta), so at runtime
            // it takes an appBuildType branch below ('canary' — BUILD_TYPE is unset in
            // build-web.sh / the box build, debug=false). A degraded/pre-config render seeds from
            // whichever branch is live, so EVERY branch must carry the PIPE identity: serverName
            // 'PIPE' + OIDC-only (locked owner decision — no Google/Apple/email sign-in; upstream's
            // Google/Apple defaults rendered DEAD sign-in buttons after a site-data clear).
            serverName: 'PIPE',
            features: [],
            oauthProviders: [OAuthProviderType.OIDC],
            type: ServerDeploymentType.Selfhosted,
            credentialsRequirement: {
              password: {
                minLength: 8,
                maxLength: 32,
              },
            },
          },
        },
      ]
    : BUILD_CONFIG.debug
      ? [
          {
            id: 'pipe-cloud',
            baseUrl: BUILD_CONFIG.isElectron
              ? 'http://localhost:8080'
              : nativePipeOrBrowserOrigin(),
            config: {
              serverName: 'PIPE',
              features: [
                ServerFeature.Indexer,
                ServerFeature.Copilot,
                ServerFeature.CopilotEmbedding,
                ServerFeature.OAuth,
                ServerFeature.Payment,
                ServerFeature.LocalWorkspace,
              ],
              oauthProviders: [OAuthProviderType.OIDC],
              type: ServerDeploymentType.Affine,
              credentialsRequirement: {
                password: {
                  minLength: 8,
                  maxLength: 32,
                },
              },
            },
          },
        ]
      : BUILD_CONFIG.appBuildType === 'stable'
        ? [
            {
              id: 'pipe-cloud',
              baseUrl: nativePipeOrBrowserOrigin(),
              config: {
                serverName: 'PIPE',
                features: [
                  ServerFeature.Indexer,
                  ServerFeature.Copilot,
                  ServerFeature.CopilotEmbedding,
                  ServerFeature.OAuth,
                  ServerFeature.Payment,
                  ServerFeature.LocalWorkspace,
                ],
                oauthProviders: [OAuthProviderType.OIDC],
                type: ServerDeploymentType.Affine,
                credentialsRequirement: {
                  password: {
                    minLength: 8,
                    maxLength: 32,
                  },
                },
              },
            },
          ]
        : BUILD_CONFIG.appBuildType === 'beta'
          ? [
              {
                id: 'pipe-cloud',
                baseUrl: nativePipeOrBrowserOrigin(),
                config: {
                  serverName: 'PIPE',
                  features: [
                    ServerFeature.Indexer,
                    ServerFeature.Copilot,
                    ServerFeature.CopilotEmbedding,
                    ServerFeature.OAuth,
                    ServerFeature.Payment,
                    ServerFeature.LocalWorkspace,
                  ],
                  oauthProviders: [OAuthProviderType.OIDC],
                  type: ServerDeploymentType.Affine,
                  credentialsRequirement: {
                    password: {
                      minLength: 8,
                      maxLength: 32,
                    },
                  },
                },
              },
            ]
          : BUILD_CONFIG.appBuildType === 'internal'
            ? [
                {
                  id: 'pipe-cloud',
                  baseUrl: nativePipeOrBrowserOrigin(),
                  config: {
                    serverName: 'PIPE',
                    features: [
                      ServerFeature.Indexer,
                      ServerFeature.Copilot,
                      ServerFeature.CopilotEmbedding,
                      ServerFeature.OAuth,
                      ServerFeature.Payment,
                      ServerFeature.LocalWorkspace,
                    ],
                    oauthProviders: [OAuthProviderType.OIDC],
                    type: ServerDeploymentType.Affine,
                    credentialsRequirement: {
                      password: {
                        minLength: 8,
                        maxLength: 32,
                      },
                    },
                  },
                },
              ]
            : BUILD_CONFIG.appBuildType === 'canary'
              ? [
                  {
                    id: 'pipe-cloud',
                    baseUrl: nativePipeOrBrowserOrigin(),
                    config: {
                      serverName: 'PIPE',
                      features: [
                        ServerFeature.Indexer,
                        ServerFeature.Copilot,
                        ServerFeature.CopilotEmbedding,
                        ServerFeature.OAuth,
                        ServerFeature.Payment,
                        ServerFeature.LocalWorkspace,
                      ],
                      oauthProviders: [OAuthProviderType.OIDC],
                      type: ServerDeploymentType.Affine,
                      credentialsRequirement: {
                        password: {
                          minLength: 8,
                          maxLength: 32,
                        },
                      },
                    },
                  },
                ]
              : [];

export type TelemetryChannel =
  | 'stable'
  | 'beta'
  | 'internal'
  | 'canary'
  | 'local';

// pipe (non-negotiable #4 — no telemetry leaving the box): self-hosted pipe never reports to an
// external collector. Empty endpoints make TelemetryManager.flush() a no-op (it early-returns when
// `officialEndpoint` is unset). The upstream AFFiNE collectors (app.affine.pro, insider.affine.pro,
// affine.fail) are deliberately removed.
const OFFICIAL_TELEMETRY_ENDPOINTS: Record<TelemetryChannel, string> = {
  stable: '',
  beta: '',
  internal: '',
  canary: '',
  local: '',
};

export function getOfficialTelemetryEndpoint(
  channel = BUILD_CONFIG.appBuildType
): string {
  if (BUILD_CONFIG.debug) {
    return OFFICIAL_TELEMETRY_ENDPOINTS.local;
  } else if (['beta', 'internal', 'canary', 'stable'].includes(channel)) {
    return OFFICIAL_TELEMETRY_ENDPOINTS[channel];
  }

  return OFFICIAL_TELEMETRY_ENDPOINTS.stable;
}
